import {
    BOUNDARY_FILL_IMAGE,
    NO_ENTRY_FILL_IMAGE
} from 'components/MapBox/hooks/useImages/constants';

import useStyle from 'hooks/useStyle';

import {
    DEFAULT_CLUSTER_FONT_SIZE,
    DEFAULT_UNCLUSTERED_POINT_RADIUS,
    LAYER_TYPE,
    MAP_LAYER
} from './constants';
import type { ClusterStyle, LayerStyle, UnclusteredPointStyle } from './types';

const useLayerStyles = () => {
    const { getCSSValue } = useStyle();

    const getDrawingStyles = (): LayerStyle[] => [
        {
            name: MAP_LAYER.COMPLETED_BOUNDARY_FILL,
            style: {
                'fill-color': getCSSValue('--map-drawing-boundary-polygon-fill-color'),
                'fill-opacity': parseFloat(getCSSValue('--map-completed-polygon-fill-opacity'))
            },
            type: LAYER_TYPE.FILL
        },
        {
            name: MAP_LAYER.COMPLETED_OBSTACLE_FILL,
            style: {
                'fill-color': getCSSValue('--map-drawing-obstacle-polygon-fill-color'),
                'fill-opacity': parseFloat(getCSSValue('--map-completed-polygon-fill-opacity'))
            },
            type: LAYER_TYPE.FILL
        },
        {
            name: MAP_LAYER.DRAWING_BOUNDARY_POLYGON_FILL,
            style: {
                'fill-pattern': BOUNDARY_FILL_IMAGE
            },
            type: LAYER_TYPE.FILL
        },
        {
            name: MAP_LAYER.DRAWING_BOUNDARY_POLYGON_OUTLINE,
            style: {
                'line-color': getCSSValue('--map-drawing-boundary-polygon-outline-color'),
                'line-width': parseFloat(
                    getCSSValue('--map-drawing-boundary-polygon-outline-width')
                )
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.DRAWING_BOUNDARY_POLYGON_CURRENT_VERTEX_LINE,
            style: {
                'line-color': getCSSValue(
                    '--map-drawing-boundary-polygon-current-vertex-line-color'
                ),
                'line-dasharray': [2, 2],
                'line-width': parseFloat(
                    getCSSValue('--map-drawing-boundary-polygon-current-vertex-line-width')
                )
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.DRAWING_BOUNDARY_POLYGON_FIRST_TO_LAST_LINE,
            style: {
                'line-color': getCSSValue(
                    '--map-drawing-boundary-polygon-first-to-last-line-color'
                ),
                'line-width': parseFloat(
                    getCSSValue('--map-drawing-boundary-polygon-first-to-last-line-width')
                )
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.DRAWING_POINT,
            style: {
                'circle-color': getCSSValue('--map-drawing-point-color'),
                'circle-opacity': parseFloat(getCSSValue('--map-drawing-point-opacity')),
                'circle-radius': parseFloat(getCSSValue('--map-drawing-point-radius')),
                'circle-stroke-color': getCSSValue('--map-drawing-point-stroke-color'),
                'circle-stroke-opacity': parseFloat(
                    getCSSValue('--map-drawing-point-stroke-opacity')
                ),
                'circle-stroke-width': parseFloat(getCSSValue('--map-drawing-point-stroke-width'))
            },
            type: LAYER_TYPE.CIRCLE
        },
        {
            name: MAP_LAYER.DRAWING_BOUNDARY_CIRCLE_FILL,
            style: {
                'fill-pattern': BOUNDARY_FILL_IMAGE
            },
            type: LAYER_TYPE.FILL
        },
        {
            name: MAP_LAYER.DRAWING_BOUNDARY_CIRCLE_OUTLINE,
            style: {
                'line-color': getCSSValue('--map-drawing-boundary-circle-outline-color'),
                'line-width': parseFloat(getCSSValue('--map-drawing-boundary-circle-outline-width'))
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.DRAWING_CIRCLE_RADIUS_LINE,
            style: {
                'line-color': getCSSValue('--map-drawing-boundary-circle-outline-color'),
                'line-dasharray': [2, 2],
                'line-width': parseFloat(getCSSValue('--map-drawing-boundary-circle-outline-width'))
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.DRAWING_OBSTACLE_POLYGON_FILL,
            style: {
                'fill-pattern': NO_ENTRY_FILL_IMAGE
            },
            type: LAYER_TYPE.FILL
        },
        {
            name: MAP_LAYER.DRAWING_OBSTACLE_POLYGON_OUTLINE,
            style: {
                'line-color': getCSSValue('--map-drawing-obstacle-polygon-outline-color'),
                'line-width': parseFloat(
                    getCSSValue('--map-drawing-obstacle-polygon-outline-width')
                )
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.DRAWING_OBSTACLE_POLYGON_CURRENT_VERTEX_LINE,
            style: {
                'line-color': getCSSValue(
                    '--map-drawing-obstacle-polygon-current-vertex-line-color'
                ),
                'line-dasharray': [2, 2],
                'line-width': parseFloat(
                    getCSSValue('--map-drawing-obstacle-polygon-current-vertex-line-width')
                )
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.DRAWING_OBSTACLE_POLYGON_FIRST_TO_LAST_LINE,
            style: {
                'line-color': getCSSValue(
                    '--map-drawing-obstacle-polygon-first-to-last-line-color'
                ),
                'line-width': parseFloat(
                    getCSSValue('--map-drawing-obstacle-polygon-first-to-last-line-width')
                )
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.DRAWING_OBSTACLE_CIRCLE_FILL,
            style: {
                'fill-pattern': NO_ENTRY_FILL_IMAGE
            },
            type: LAYER_TYPE.FILL
        },
        {
            name: MAP_LAYER.DRAWING_OBSTACLE_CIRCLE_OUTLINE,
            style: {
                'line-color': getCSSValue('--map-drawing-obstacle-circle-outline-color'),
                'line-width': parseFloat(getCSSValue('--map-drawing-obstacle-circle-outline-width'))
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.RETICLE_CENTER_POINT,
            style: {
                'circle-color': getCSSValue('--map-reticle-center-point-color'),
                'circle-opacity': parseFloat(getCSSValue('--map-reticle-center-point-opacity')),
                'circle-radius': parseFloat(getCSSValue('--map-reticle-center-point-radius'))
            },
            type: LAYER_TYPE.CIRCLE
        },
        {
            name: MAP_LAYER.RETICLE_INNER_CIRCLE_PERIMETER,
            style: {
                'circle-color': getCSSValue('--map-reticle-inner-circle-perimeter-color'),
                'circle-opacity': 0,
                'circle-radius': parseFloat(
                    getCSSValue('--map-reticle-inner-circle-perimeter-radius')
                ),
                'circle-stroke-color': getCSSValue('--map-reticle-inner-circle-perimeter-color'),
                'circle-stroke-opacity': 1,
                'circle-stroke-width': parseFloat(
                    getCSSValue('--map-reticle-inner-circle-perimeter-width')
                )
            },
            type: LAYER_TYPE.CIRCLE
        },
        {
            name: MAP_LAYER.RETICLE_OUTER_CIRCLE_PERIMETER,
            style: {
                'circle-color': getCSSValue('--map-reticle-outer-circle-perimeter-color'),
                'circle-opacity': 0,
                'circle-radius': parseFloat(
                    getCSSValue('--map-reticle-outer-circle-perimeter-radius')
                ),
                'circle-stroke-color': getCSSValue('--map-reticle-outer-circle-perimeter-color'),
                'circle-stroke-opacity': parseFloat(
                    getCSSValue('--map-reticle-outer-circle-perimeter-opacity')
                ),
                'circle-stroke-width': parseFloat(
                    getCSSValue('--map-reticle-outer-circle-perimeter-radius')
                )
            },
            type: LAYER_TYPE.CIRCLE
        },
        {
            name: MAP_LAYER.DRAWING_BOUNDING_BOX_OUTLINE,
            style: {
                'line-color': getCSSValue('--map-bounding-box-outline-color'),
                'line-width': parseFloat(getCSSValue('--map-bounding-box-outline-width'))
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.DRAWING_OBSTACLE_BOUNDING_BOX_OUTLINE,
            style: {
                'line-color': getCSSValue('--map-no-entry-outline-color'),
                'line-width': parseFloat(getCSSValue('--map-bounding-box-outline-width'))
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.FLIGHT_PATH_FILL,
            style: {
                'fill-color': getCSSValue('--map-flight-path-fill-color'),
                'fill-opacity': parseFloat(getCSSValue('--map-flight-path-fill-opacity'))
            },
            type: LAYER_TYPE.FILL
        },
        {
            name: MAP_LAYER.FLIGHT_PATH_LINE,
            style: {
                'line-color': getCSSValue('--map-flight-path-line-color'),
                'line-opacity': parseFloat(getCSSValue('--map-flight-path-line-opacity')),
                'line-width': 2
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.FLIGHT_PATH_HIGHLIGHT_FILL,
            style: {
                'fill-color': getCSSValue('--map-flight-path-highlight-fill-color'),
                'fill-opacity': parseFloat(getCSSValue('--map-flight-path-highlight-fill-opacity'))
            },
            type: LAYER_TYPE.FILL
        },
        {
            name: MAP_LAYER.FLIGHT_PATH_HIGHLIGHT_LINE,
            style: {
                'line-color': getCSSValue('--map-flight-path-highlight-line-color'),
                'line-opacity': parseFloat(getCSSValue('--map-flight-path-highlight-line-opacity')),
                'line-width': 3
            },
            type: LAYER_TYPE.LINE
        }
    ];

    const getStaticGeometryStyles = (): LayerStyle[] => [
        {
            name: MAP_LAYER.STATIC_BOUNDARY_FILL,
            style: {
                'fill-color': getCSSValue('--map-drawing-boundary-polygon-fill-color'),
                'fill-opacity': parseFloat(getCSSValue('--map-completed-polygon-fill-opacity'))
            },
            type: LAYER_TYPE.FILL
        },
        {
            filter: MAP_LAYER.STATIC_BOUNDARY_FILL,
            name: MAP_LAYER.STATIC_BOUNDARY_OUTLINE,
            style: {
                'line-color': getCSSValue('--map-completed-polygon-outline-color'),
                'line-width': parseFloat(getCSSValue('--map-completed-polygon-outline-width'))
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.STATIC_OBSTACLE_FILL,
            style: {
                'fill-color': getCSSValue('--map-drawing-obstacle-polygon-fill-color'),
                'fill-opacity': parseFloat(getCSSValue('--map-completed-polygon-fill-opacity'))
            },
            type: LAYER_TYPE.FILL
        },
        {
            name: MAP_LAYER.STATIC_BOUNDING_BOX_OUTLINE,
            style: {
                'line-color': getCSSValue('--map-bounding-box-outline-color'),
                'line-width': parseFloat(getCSSValue('--map-bounding-box-outline-width'))
            },
            type: LAYER_TYPE.LINE
        },
        {
            name: MAP_LAYER.STATIC_OBSTACLE_BOUNDING_BOX_OUTLINE,
            style: {
                'line-color': getCSSValue('--map-no-entry-outline-color'),
                'line-width': parseFloat(getCSSValue('--map-bounding-box-outline-width'))
            },
            type: LAYER_TYPE.LINE
        }
    ];

    const getClusterPointStyle = (): ClusterStyle => ({
        clusterRadiusSteps: [22, 26, 30],
        clusterSteps: [
            getCSSValue('--map-cluster-background-color'),
            10,
            getCSSValue('--map-cluster-background-color'),
            25,
            getCSSValue('--map-cluster-background-color')
        ],
        font: {
            color: getCSSValue('--map-cluster-font-color'),
            size: DEFAULT_CLUSTER_FONT_SIZE,
            type: ['Open Sans Bold', 'Arial Unicode MS Bold']
        }
    });

    const getUnclusteredPointStyle = (): UnclusteredPointStyle => ({
        color: getCSSValue('--map-cluster-background-color'),
        font: {
            color: getCSSValue('--map-cluster-font-color'),
            size: DEFAULT_CLUSTER_FONT_SIZE,
            type: ['Open Sans Bold', 'Arial Unicode MS Bold']
        },
        radius: DEFAULT_UNCLUSTERED_POINT_RADIUS
    });

    return {
        getClusterPointStyle,
        getDrawingStyles,
        getStaticGeometryStyles,
        getUnclusteredPointStyle
    };
};

export default useLayerStyles;
